import { ColDef } from 'ag-grid-community';
import { LicenseRenewalWorkflow } from '@workspace-app/odatabackend/QualicoatBackendModel';
import { StatusCellRendererComponent } from '../../components/status-cell-renderer.component';

export function getLicenseRenewalWorkflowsColumns(): ColDef[] {
  const columns: ColDef[] = [];

  // Review Number - Hidden by default
  columns.push({
    headerName: 'Review #',
    field: 'reviewNumber',
    minWidth: 120,
    width: 150,
    maxWidth: 200,
    sort: 'desc',
    cellClass: 'font-semibold',
  });

  // Year
  columns.push({
    headerName: 'Year',
    field: 'certificationYear',
    minWidth: 80,
    width: 180,
    maxWidth: 200,
  });

  // Round
  columns.push({
    headerName: 'Round',
    field: 'reviewRound',
    minWidth: 80,
    width: 180,
    maxWidth: 200,
  });

  // Status
  columns.push({
    headerName: 'Status',
    field: 'currentState',
    minWidth: 150,
    maxWidth: 200,
    cellRenderer: StatusCellRendererComponent,
  });

  // Progress
  columns.push({
    headerName: 'Progress',
    field: 'progress',
    minWidth: 140,
    maxWidth: 160,
    valueGetter: (params: { data: LicenseRenewalWorkflow }) => {
      const review = params.data as LicenseRenewalWorkflow;
      return calculateProgress(review);
    },
    cellRenderer: (params: { value: number }) => {
      const progress = params.value;
      return `
        <div style="display: flex; align-items: center; height: 100%;">
          <div class="progress-bar" style="width: 120px; height: 8px; background: #e0e0e0; border-radius: 4px;">
            <div style="width: ${progress}%; height: 100%; background: #4CAF50; border-radius: 4px;"></div>
          </div>
        </div>
      `;
    },
  });

  // Candidates
  columns.push({
    headerName: 'Candidates',
    minWidth: 120,
    width: 180,
    maxWidth: 200,
    valueGetter: (params: { data: LicenseRenewalWorkflow }) => {
      const review = params.data as LicenseRenewalWorkflow;
      return `${review.selectedCandidatesCount || 0} / ${review.totalCandidatesCount || 0}`;
    },
  });

  // Extended Licenses
  columns.push({
    headerName: 'Renewed Licenses',
    field: 'extendedLicensesCount',
    minWidth: 100,
    width: 180,
    maxWidth: 200,
    valueFormatter: (params: { value?: number }) => params.value?.toString() || '-',
  });

  // Created On
  columns.push({
    headerName: 'Created On',
    field: 'createdOn',
    minWidth: 120,
    width: 180,
    maxWidth: 200,
    valueFormatter: (params: { value?: string }) => {
      if (params.value) {
        return new Date(params.value).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          timeZone: 'UTC',
        });
      }
      return '-';
    },
  });

  // Created By - uses expanded Creator navigation property
  columns.push({
    headerName: 'Created By',
    field: 'creator.displayName',
    minWidth: 200,
    flex: 1, // This column will expand to fill available space
    valueGetter: (params: { data: LicenseRenewalWorkflow }) => {
      return params.data?.creator?.displayName || '-';
    },
  });

  return columns;
}

// Helper function to calculate progress based on simplified workflow
function calculateProgress(review: LicenseRenewalWorkflow): number {
  const stateProgress: { [key: string]: number } = {
    Created: 0, // Step 1: Candidates Selection
    Review: 25, // Step 2: License Renewal
    IssuingCertificates: 50, // Step 3: Certificate Issuing
    FinalReview: 75, // Step 4: Final Review
    Completed: 100, // Finalized
    Cancelled: 0,
  };

  return stateProgress[review.currentState] || 0;
}
